/* eslint-disable max-lines */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import {
  Connect,
  PropsFromRedux,
} from './SearchQuerySectionCommentariesContainer';

import { FiltersToggle } from '../../../../../../../components/filters-toggle/FiltersToggle';
import { Loader } from '../../../../../../../components/loader/Loader';
import { SearchBarSection } from '../../search-bar-section/SearchBarSection';
import { ResetQueryMessage } from '../ResetQueryMessage';
import { RawSearchResultCommentaries } from '../../../../../../../search/components/search-results/commentary/SearchResultCommentaries';
import { SeeMoreMessage } from '../SeeMoreMessage';

import { useUser } from '../../../../../../../user/useUser';
import { searchWithinRestrictedArticles } from '../../../../../legislation.api';
import {
  buildArticleFilters,
  getBOFIPAggregation,
  shouldBeSortedByChrono,
  getParliamentaryQuestionsAggregation,
} from '../utils/utils';
import {
  BOFIPFilter,
  parliamentaryQuestionsFilter,
} from '../../../utils/constants';
import { doctrineAnalytics } from '../../../../../../../utils/analyticsUtils';
import { useRestrictedModal } from '../../../../../../../components/modal/useRestrictedModal';
import { getAggregations } from '../../../../../../../search/state/search.api';
import { LegislationContextType } from '../../../../../legislation.types';

import styles from './search_query_section_commentaries.module.scss';

type Props = {
  commentaries: LegislationContextType['commentaries'];
  articleTitle: string;
  articleIds: string[];
  articleId: string;
  initialVersionId: string;
  textNature: string;
  textId: string;
} & PropsFromRedux;

export const RawSearchQuerySectionCommentaries: FC<Props> = ({
  articleTitle,
  articleIds,
  articleId,
  initialVersionId,
  query,
  commentaries,
  textId,
  textNature,
}) => {
  const { openRestrictedModal } = useRestrictedModal();
  const { loggedIn } = useUser();
  const intl = useIntl();
  const { search } = useLocation();
  const shouldPersistQuery = !!(loggedIn && query);
  const searchParams = new URLSearchParams(search);
  const filterParam = searchParams.get('filter');

  const [loading, setLoading] = useState(false);
  const [resultHits, setResultHits] = useState(
    shouldPersistQuery ? [] : commentaries.data?.hits,
  );
  const [currentQuery, setCurrentQuery] = useState<string>(query);
  const [filterValue, setFilterValue] = useState<string>('sort_chrono');
  const [aggregationsFilters, setAggregationsFilters] = useState({
    pq: commentaries.filters?.pq || { count: 0 },
    bofip: commentaries.filters?.bofip || { count: 0 },
  });
  const [resultTotalHits, setResultTotalHits] = useState(
    shouldPersistQuery ? 0 : commentaries.data?.totalHits,
  );
  const [resultTheEnd, setResultTheEnd] = useState(
    shouldPersistQuery ? false : commentaries.data?.theEnd,
  );

  const getPublisherFilter = (value: string) =>
    (value === 'parliamentary_questions' && parliamentaryQuestionsFilter) ||
    (value === 'bofip' && BOFIPFilter) ||
    undefined;

  const memoizedFilters = useMemo(
    () =>
      buildArticleFilters({
        type: 'commentary',
        loggedIn,
        aggregations: aggregationsFilters,
        intl,
        textId,
      }),
    [intl, loggedIn, aggregationsFilters, textId],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await searchWithinRestrictedArticles({
        q: currentQuery,
        type: 'commentary',
        restrict_to_law_article_ids: articleIds,
        chrono: true,
        size: 8,
      });
      const res = (
        await getAggregations({
          q: currentQuery,
          type: 'commentary',
          restrict_to_law_article_ids: articleIds,
          publisher: parliamentaryQuestionsFilter,
        })
      ).data;

      setAggregationsFilters({
        ...aggregationsFilters,
        pq: getParliamentaryQuestionsAggregation(res),
        bofip: getBOFIPAggregation(res),
      });

      setResultHits(result.hits);
      setResultTotalHits(result.totalHits);
      setResultTheEnd(result.theEnd);
      setLoading(false);
    };

    if (shouldPersistQuery && query.length) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClearClicked = async () => {
    setLoading(true);
    const result = await searchWithinRestrictedArticles(
      {
        type: 'commentary',
        restrict_to_law_article_ids: articleIds,
        size: 8,
        chrono: shouldBeSortedByChrono(filterValue),
        publisher: getPublisherFilter(filterValue),
      },
      loggedIn,
    );
    setAggregationsFilters({
      pq: commentaries.filters?.pq || { count: 0 },
      bofip: commentaries.filters?.bofip || { count: 0 },
    });
    setResultHits(result.hits);
    setResultTotalHits(result.totalHits);
    setResultTheEnd(result.theEnd);
    setCurrentQuery('');
    setLoading(false);
  };

  const onFilterClick = async (value: string) => {
    if (!loggedIn) {
      openRestrictedModal({ source: `commentary_${value}` });
      return;
    }
    doctrineAnalytics('Legislation CommentaryCategoryClicked', {
      category_name: value,
      article_version_id: articleId,
      article_id: initialVersionId,
      legislationType: textNature,
    });
    setFilterValue(value);
    setLoading(true);
    const result = await searchWithinRestrictedArticles({
      q: currentQuery,
      type: 'commentary',
      restrict_to_law_article_ids: articleIds,
      size: 8,
      chrono: shouldBeSortedByChrono(value),
      publisher: getPublisherFilter(value),
    });
    setResultHits(result.hits);
    setResultTotalHits(result.totalHits);
    setResultTheEnd(result.theEnd);
    setLoading(false);
  };

  const renderFilterResults = () => {
    if (loading) return <Loader color="gray" size={48} strokeWidth={4} />;

    if (!resultHits?.length)
      return (
        <ResetQueryMessage
          type="commentary"
          translationId="page/components/SearchQuerySection.tsx/6"
          articleTitle={articleTitle}
          query={currentQuery}
          onResetQueryMessageClicked={onClearClicked}
        />
      );
    if (resultHits) {
      return (
        <>
          <RawSearchResultCommentaries
            // @ts-ignore
            commentaries={resultHits}
            enableSnippets={false}
          />
          {!resultTheEnd && (
            <SeeMoreMessage
              type="commentary"
              query={currentQuery}
              articleIds={articleIds}
              loggedIn={loggedIn}
              filterValue={filterValue}
              resultTotalHits={resultTotalHits || 0}
              modal="commentaries-search"
            />
          )}
          {resultTheEnd && currentQuery && (
            <ResetQueryMessage
              type="commentary"
              translationId="page/components/SearchQuerySection.tsx/12"
              articleTitle={articleTitle}
              query={currentQuery}
              onResetQueryMessageClicked={onClearClicked}
            />
          )}
        </>
      );
    }
  };
  return (
    <>
      <SearchBarSection
        articleIds={articleIds}
        modal="commentaries-search"
        type="commentary"
        filterValue={filterValue}
        queryFromState={currentQuery}
        shouldPersistQuery={shouldPersistQuery}
        onClearClicked={onClearClicked}
      />
      <div className={styles.filtersToggleContainer}>
        {resultHits?.length ? (
          <FiltersToggle
            filters={memoizedFilters}
            filterParam={filterParam}
            onFilterClick={(value) => onFilterClick(value)}
            disableFilterClick={!loggedIn}
          />
        ) : null}
      </div>
      {renderFilterResults()}
    </>
  );
};

export const SearchQuerySectionCommentaries = Connect(
  RawSearchQuerySectionCommentaries,
);
